import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Divider, Alert, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import verificareToken from '../VerificareToken';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

function IstoricDosareConexe({ idProp }) {
  const { id: idFromParams } = useParams();
  const id = idProp || idFromParams;

  const [dosareConexe, setDosareConexe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { token } = verificareToken();

  useEffect(() => {
    const fetchDosareConexe = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${window.API_URL}legaturiDosare/${id}/detaliat`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Eroare la preluarea istoricului dosarelor conexe');
        }

        const result = await response.json();
        if (result && result.data) {
          setDosareConexe(result.data);
        }
      } catch (err) {
        console.error('Eroare la preluarea dosarelor conexe:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id && token) {
      fetchDosareConexe();
    }
  }, [id, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd.MM.yyyy HH:mm', { locale: ro });
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd.MM.yyyy', { locale: ro });
    } catch {
      return dateStr;
    }
  };

  const handleDeschideDosar = (dosarId) => {
    window.open(`/editare-dosar/${dosarId}`, '_blank');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress size={28} sx={{ color: '#8e8e93 !important' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: '16px !important', borderRadius: '10px !important' }}>
        {error}
      </Alert>
    );
  }

  if (!dosareConexe || dosareConexe.length === 0) {
    return (
      <Alert severity="info" sx={{ m: '16px !important', borderRadius: '10px !important' }}>
        Nu există dosare conexe pentru acest dosar.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100% !important', fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif !important' }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: '20px !important', 
          color: '#6e6e73 !important',
          fontSize: '0.8rem !important',
          fontWeight: '500 !important',
          letterSpacing: '0.02em !important',
          textTransform: 'uppercase !important',
        }}
      >
        Total dosare conexe: {dosareConexe.length}
      </Typography>

      {dosareConexe.map((dosar) => (
        <Accordion 
          key={dosar.id} 
          sx={{ 
            mb: '8px !important',
            bgcolor: '#f5f5f7 !important',
            border: '1px solid #e8e8ed !important',
            borderRadius: '12px !important',
            boxShadow: 'none !important',
            overflow: 'hidden !important',
            transition: 'all 0.2s ease !important',
            '& .MuiCollapse-vertical': {
              backgroundColor: 'transparent !important',
            },
            '&:hover': {
              bgcolor: '#f0f0f5 !important',
              borderColor: '#d2d2d7 !important',
            },
            '&:before': {
              display: 'none !important',
            },
            '&.Mui-expanded': {
              bgcolor: '#ffffff !important',
              border: '1px solid #d2d2d7 !important',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04) !important',
              margin: '0 0 8px 0 !important',
            },
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: '#86868b !important', fontSize: '1.2rem !important' }} />}
            sx={{
              minHeight: '52px !important',
              px: '20px !important',
              backgroundImage: 'none !important',
              backgroundColor: 'transparent !important',
              animation: 'none !important',
              WebkitAnimation: 'none !important',
              color: '#1d1d1f !important',
              '& .MuiAccordionSummary-content': {
                my: '12px !important',
              },
              '& p': {
                color: '#1d1d1f !important',
              },
              '& .MuiTypography-root': {
                color: '#1d1d1f !important',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px !important', flexWrap: 'wrap', width: '100% !important' }}>
              <GavelIcon fontSize="small" sx={{ color: '#1d1d1f !important', opacity: '0.7 !important' }} />
              <Typography variant="body2" sx={{ fontWeight: '600 !important', color: '#1d1d1f !important', fontSize: '0.875rem !important' }}>
                {dosar.numarDosarUnic}
              </Typography>
              <Chip
                label={dosar.materie}
                size="small"
                sx={{
                  bgcolor: (dosar.materie === 'Penal' ? '#fff1f0' : '#f0f5ff') + ' !important',
                  color: (dosar.materie === 'Penal' ? '#cf1322' : '#0958d9') + ' !important',
                  border: 'none !important',
                  fontWeight: '500 !important',
                  fontSize: '0.7rem !important',
                  height: '22px !important',
                  borderRadius: '6px !important',
                }}
              />
              <Tooltip title="Deschide dosarul">
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleDeschideDosar(dosar.id); }}
                  sx={{ 
                    ml: '4px !important',
                    color: '#86868b !important',
                    width: '28px !important',
                    height: '28px !important',
                    borderRadius: '7px !important',
                    transition: 'all 0.15s ease !important',
                    '&:hover': { 
                      bgcolor: '#e8e8ed !important',
                      color: '#1d1d1f !important',
                    } 
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: '0.95rem !important' }} />
                </IconButton>
              </Tooltip>
              <Typography 
                variant="caption" 
                sx={{ 
                  ml: 'auto !important', 
                  mr: '8px !important', 
                  color: '#86868b !important',
                  fontSize: '0.75rem !important',
                  fontWeight: '400 !important',
                }}
              >
                Creat: {formatDateShort(dosar.dataCreare)}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: '20px !important', pt: '0 !important', pb: '20px !important' }}>
            <Box sx={{ 
              mb: '16px !important', 
              p: '16px !important', 
              bgcolor: '#fafafa !important', 
              borderRadius: '8px !important',
              border: '1px solid #f0f0f0 !important',
            }}>
              <Typography variant="body2" sx={{ color: '#424245 !important', lineHeight: '1.6 !important', fontSize: '0.82rem !important' }} gutterBottom>
                <span style={{ fontWeight: 600, color: '#1d1d1f' }}>Obiect:</span> {dosar.obiect}
              </Typography>
              {dosar.user && (
                <Typography variant="body2" sx={{ color: '#6e6e73 !important', display: 'flex', alignItems: 'center', gap: '4px !important', mt: '8px !important', fontSize: '0.8rem !important' }}>
                  <PersonIcon sx={{ fontSize: '0.95rem !important', color: '#86868b !important' }} /> {dosar.user.fullname}
                </Typography>
              )}
            </Box>

            {dosar.stadii && dosar.stadii.map((stadiu) => (
              <Box key={stadiu.id} sx={{ mb: '16px !important', pl: '16px !important', borderLeft: '2px solid #d2d2d7 !important' }}>
                <Typography variant="subtitle2" sx={{ mb: '12px !important', color: '#1d1d1f !important', fontWeight: '600 !important', fontSize: '0.82rem !important' }}>
                  {stadiu.denumire} — {formatDateShort(stadiu.data)}
                </Typography>

                {stadiu.parti && stadiu.parti.length > 0 && (
                  <Box sx={{ mb: '12px !important' }}>
                    <Typography variant="caption" sx={{ color: '#86868b !important', fontWeight: '600 !important', fontSize: '0.7rem !important', textTransform: 'uppercase !important', letterSpacing: '0.03em !important' }}>
                      Părți
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px !important', mt: '4px !important' }}>
                      {stadiu.parti.map((parte) => (
                        <Chip
                          key={parte.id}
                          label={`${parte.numePrenume} (${parte.calitate})`}
                          size="small"
                          sx={{ 
                            fontSize: '0.68rem !important', 
                            bgcolor: '#f5f5f7 !important', 
                            color: '#424245 !important',
                            border: '1px solid #e8e8ed !important',
                            borderRadius: '6px !important',
                            fontWeight: '400 !important',
                            height: '24px !important',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {stadiu.termene && stadiu.termene.length > 0 && (
                  <Box sx={{ mt: '12px !important' }}>
                    <Typography variant="caption" sx={{ color: '#86868b !important', fontWeight: '600 !important', fontSize: '0.7rem !important', textTransform: 'uppercase !important', letterSpacing: '0.03em !important' }}>
                      Termene ({stadiu.termene.length})
                    </Typography>
                    <Box sx={{ 
                      maxHeight: '300px !important', 
                      overflowY: 'auto !important', 
                      mt: '4px !important',
                      '&::-webkit-scrollbar': {
                        width: '6px !important',
                      },
                      '&::-webkit-scrollbar-track': {
                        bgcolor: 'transparent !important',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#d2d2d7 !important',
                        borderRadius: '3px !important',
                      },
                    }}>
                      {stadiu.termene.map((termen) => (
                        <Box
                          key={termen.id}
                          sx={{
                            p: '12px !important',
                            mb: '4px !important',
                            bgcolor: '#ffffff !important',
                            borderRadius: '8px !important',
                            border: '1px solid #e8e8ed !important',
                            transition: 'all 0.15s ease !important',
                            '&:hover': {
                              bgcolor: '#fafafa !important',
                              borderColor: '#d2d2d7 !important',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px !important', mb: '4px !important' }}>
                            <CalendarTodayIcon sx={{ fontSize: '0.85rem !important', color: '#86868b !important' }} />
                            <Typography variant="body2" sx={{ fontWeight: '600 !important', color: '#1d1d1f !important', fontSize: '0.8rem !important' }}>
                              {formatDate(termen.dataTermen)}
                            </Typography>
                            {termen.tipSolutie && (
                              <Chip
                                label={termen.tipSolutie}
                                size="small"
                                sx={{ 
                                  fontSize: '0.65rem !important', 
                                  bgcolor: '#f5f5f7 !important',
                                  color: '#6e6e73 !important',
                                  border: '1px solid #e8e8ed !important',
                                  borderRadius: '5px !important',
                                  fontWeight: '500 !important',
                                  height: '20px !important',
                                }}
                              />
                            )}
                          </Box>
                          {termen.sumarSolutie && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block !important',
                                whiteSpace: 'pre-wrap !important',
                                maxHeight: '100px !important',
                                overflowY: 'auto !important',
                                pl: '24px !important',
                                color: '#6e6e73 !important',
                                fontSize: '0.72rem !important',
                                lineHeight: '1.5 !important',
                              }}
                            >
                              {termen.sumarSolutie}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                <Divider sx={{ mt: '16px !important', borderColor: '#e8e8ed !important' }} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default IstoricDosareConexe;
